<script setup lang="ts">
import { computed, nextTick, onUnmounted, ref, watch } from 'vue';
import { DgrCheckbox, DgrIcon } from '@stockmarkteam/donguri-ui';
import SegmentedControl from '@/components/common/molecules/segmented-control.vue';
import { DocType, SearchScope } from '@/types';
import { useLayoutScrollContainer } from '@/utils/composables/useLayoutScrollContainer';
import { useOutsideClick } from '@/utils/composables/useOutsideClick';
import {
  DOC_TYPES_FOR_SCOPE,
  TARGET_SOURCE_LABELS,
} from '@/utils/survey/constants';
import { useTeamInfo } from '@/utils/swr';
import SurveyGroupSelector from './survey-group-selector.vue';

/**
 * Props
 */

interface Props {
  searchScope: SearchScope;
  docTypes: Array<DocType | 'user_document'>;
  groupId?: number;
  groupTagIds?: number[];
}

const props = defineProps<Props>();

/**
 * Emits
 */

const emit = defineEmits<{
  'apply-filter': [
    value: {
      searchScope: SearchScope;
      docTypes: string[];
      groupId?: number;
      groupTagIds?: number[];
    },
  ];
  'reset-filter': [];
}>();

/**
 * Local State
 */

// 契約状態
const { data: teamInfo } = useTeamInfo();
const enableTechnicalLiterature = computed(
  () => teamInfo.value?.enable_technical_literature ?? false,
);
const enableUserDocument = computed(
  () => teamInfo.value?.enable_user_document ?? false,
);

// フィルターの開閉状態と操作
const isOpen = ref(false);
const containerRef = ref<Element>();
const popupRef = ref<Element>();
const popupStyle = ref<{ left: string; top: string }>({
  left: '0px',
  top: '0px',
});
let scrollHandler: (() => void) | undefined;
let resizeHandler: (() => void) | undefined;

// フォームの値と操作
const internalSearchScope = ref<SearchScope>(props.searchScope);
const internalDocTypes = ref<Array<DocType | 'user_document'>>(
  sortDocTypes(props.docTypes, props.searchScope),
);
const internalGroupId = ref<number | undefined>(props.groupId);
const internalGroupTagIds = ref<number[]>(props.groupTagIds ?? []);

const isFiltering = computed(
  () => JSON.stringify(props.docTypes) !== JSON.stringify([]),
);

watch(
  () => props.searchScope,
  newVal => {
    internalSearchScope.value = newVal;
  },
);

watch(
  () => props.docTypes,
  newVal => {
    internalDocTypes.value = sortDocTypes(newVal, props.searchScope);
  },
);

watch(
  () => props.groupId,
  newVal => {
    internalGroupId.value = newVal;
  },
);

watch(
  () => props.groupTagIds,
  newVal => {
    internalGroupTagIds.value = newVal ?? [];
  },
);

// フィルターのクリックイベント(フィルターの外側をクリックした場合は閉じる)
const isClickedOutsideFilter = useOutsideClick([containerRef, '.tag-select']);
watch(isClickedOutsideFilter, () => {
  if (isClickedOutsideFilter.value) {
    closeFilter();
  }
});

/**
 * Handlers
 */
const { scrollContainer } = useLayoutScrollContainer();

const openFilter = () => {
  isOpen.value = true;
  nextTick(() => {
    updatePopupPosition();
    scrollHandler = () => updatePopupPosition();
    resizeHandler = () => updatePopupPosition();
    scrollContainer.value.addEventListener('scroll', scrollHandler);
    window.addEventListener('resize', resizeHandler);
  });
};

const closeFilter = (reset = true) => {
  isOpen.value = false;
  if (reset) {
    restoreFormValue();
  }
  if (scrollHandler) {
    scrollContainer.value.removeEventListener('scroll', scrollHandler);
    scrollHandler = undefined;
  }
  if (resizeHandler) {
    window.removeEventListener('resize', resizeHandler);
    resizeHandler = undefined;
  }
};
const toggleFilter = () => {
  if (isOpen.value) closeFilter();
  else openFilter();
};

const updatePopupPosition = () => {
  const containerEl = containerRef.value;
  const popupEl = popupRef.value;
  if (!containerEl || !popupEl) return;

  const containerRect = containerEl.getBoundingClientRect();
  const popupHeight = (popupEl as HTMLElement).offsetHeight;
  if (
    window.innerHeight - containerRect.bottom < popupHeight &&
    containerRect.top > popupHeight
  ) {
    popupStyle.value = {
      left: containerRect.left + 'px',
      top: containerRect.top - popupHeight + 'px',
    };
  } else {
    popupStyle.value = {
      left: containerRect.left + 'px',
      top: containerRect.bottom + 'px',
    };
  }
};

const toggleCheckbox = (option: DocType | 'user_document') => {
  internalDocTypes.value = internalDocTypes.value.includes(option)
    ? internalDocTypes.value.filter(v => v !== option)
    : [...internalDocTypes.value, option];
};

const restoreFormValue = () => {
  internalSearchScope.value = props.searchScope;
  internalDocTypes.value = sortDocTypes(props.docTypes, props.searchScope);
  internalGroupId.value = props.groupId;
  internalGroupTagIds.value = props.groupTagIds ?? [];
};

const resetFormValue = async () => {
  emit('reset-filter');

  await nextTick();
  closeFilter();
};

const applyFilterWithClose = async () => {
  if (internalDocTypes.value.length === 0) {
    resetFormValue();
    return;
  }

  internalDocTypes.value = sortDocTypes(
    internalDocTypes.value.filter(d =>
      DOC_TYPES_FOR_SCOPE(
        internalSearchScope.value,
        enableTechnicalLiterature.value,
        enableUserDocument.value,
      ).includes(d),
    ),
    internalSearchScope.value,
  );

  await nextTick();
  emit('apply-filter', {
    searchScope: internalSearchScope.value,
    docTypes: internalDocTypes.value,
    groupId: internalGroupId.value,
    groupTagIds: internalGroupTagIds.value,
  });
  closeFilter(false);
};

// チェックボックス（TARGET_SOURCE_LABELS）の並び順に合わせてソートする
function sortDocTypes(
  docTypes: Array<DocType | 'user_document'>,
  searchScope: SearchScope,
) {
  const order = DOC_TYPES_FOR_SCOPE(
    searchScope,
    enableTechnicalLiterature.value,
    enableUserDocument.value,
  );
  return [...docTypes].sort((a, b) => order.indexOf(a) - order.indexOf(b));
}

onUnmounted(() => {
  if (scrollHandler) {
    scrollContainer.value.removeEventListener('scroll', scrollHandler);
    scrollHandler = undefined;
  }
  if (resizeHandler) {
    window.removeEventListener('resize', resizeHandler);
    resizeHandler = undefined;
  }
});
</script>

<template>
  <div ref="containerRef" class="filter-container">
    <button class="filter-toggle-button" @click="toggleFilter">
      <span class="c-text-regular--m">{{
        isFiltering ? '絞り込み適用中' : '検索対象絞り込み'
      }}</span>
      <DgrIcon name="angle-down" class="c-selectBox__arrow" />
    </button>
    <div
      ref="popupRef"
      v-if="isOpen"
      @click.stop="() => undefined"
      class="popup-container"
      :style="popupStyle"
    >
      <div class="section-container">
        <SegmentedControl
          class="search-scope-control"
          v-model="internalSearchScope"
          :segments="[
            { label: 'すべての情報', value: 'all' },
            { label: 'マークした情報', value: 'user_marks' },
            { label: 'グループマークした情報', value: 'group_marks' },
          ]"
        />
        <div class="section-title c-text-regular--m">情報種別</div>
        <div class="doc-type-container c-text-regular--m">
          <template
            v-for="option in TARGET_SOURCE_LABELS(
              internalSearchScope,
              enableTechnicalLiterature,
              enableUserDocument,
            )"
            :key="option.value"
          >
            <DgrCheckbox
              :model-value="internalDocTypes.includes(option.value)"
              @update:model-value="() => toggleCheckbox(option.value)"
            >
              {{ option.label }}
            </DgrCheckbox>
          </template>
        </div>
        <template v-if="internalSearchScope === 'group_marks'">
          <SurveyGroupSelector
            v-model:id="internalGroupId"
            v-model:tags="internalGroupTagIds"
          />
        </template>
      </div>
      <div class="button-container">
        <button
          class="dashboard-button--borderless"
          @click="resetFormValue"
          data-testid="reset-button"
        >
          リセット
        </button>
        <div class="right-buttons">
          <button
            class="dashboard-button"
            @click="() => closeFilter()"
            data-testid="cancel-button"
          >
            キャンセル
          </button>
          <div class="spacing-16"></div>
          <button
            class="dashboard-button--primary"
            @click="applyFilterWithClose"
            data-testid="apply-button"
          >
            適用
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.filter-container {
  position: relative;
}

.filter-toggle-button {
  display: flex;
  align-items: center;
  color: $color-gray1000;
  background: #fff;
  height: 32px;
  border: 1px solid $color-border;
  width: 100%;
  justify-content: space-between;
  padding: 4px 8px;
  gap: 4px;
}

.popup-container {
  position: fixed;
  z-index: var(--z-dropdown);
  box-shadow: 0 1px 5px rgba($color-gray1000, 0.25);
  background: $color-white;
  width: 592px;
  border-radius: 4px;
}

.section-container {
  padding: 16px;
  max-height: 320px;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: flex-start;
}

.section-title {
  margin: 16px 0 4px 0;
}

.doc-type-container {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
}

.option-container {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.button-container {
  width: 100%;
  display: flex;
  justify-content: space-between;
  box-sizing: border-box;
  padding: 16px;
  border-top: 1px solid $color-gray400;
}

.right-buttons {
  display: flex;
}

.dashboard-button--borderless {
  color: $color-green600;
}

.search-scope-switch {
  width: 280px;
  margin-bottom: 16px;
}
.search-scope-control {
  width: max-content;
}
</style>
